/**
 * kpop/marquee — constant speed, and enough content to loop seamlessly.
 *
 * Two problems the server cannot solve, because both need measurements:
 *
 *  - Speed. The animation is a fixed -50% translate, so with a duration set in
 *    CSS a band of three items crawls while a band of twelve races. Deriving
 *    the duration from the measured width keeps pixels-per-second constant.
 *
 *  - Short content. With one upcoming event the track is narrower than the
 *    screen, and half of it is narrower still, so the loop shows a gap. Copies
 *    are appended in pairs until the track is at least twice the visible width;
 *    pairs keep the halves identical, which is what -50% depends on.
 */

const MAX_CLONE_PASSES = 12;

export function initMarquee() {
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

	document.querySelectorAll('.marquee').forEach((marquee) => {
		const track = marquee.querySelector('.marquee-track');
		const original = track?.querySelector('.marquee-run');
		if (!track || !original) return;

		const speed = Number(marquee.dataset.marqueeSpeed) || 60; // px per second

		const fill = () => {
			let passes = 0;
			while (track.scrollWidth < marquee.clientWidth * 2 && passes < MAX_CLONE_PASSES) {
				for (let i = 0; i < 2; i++) {
					const copy = original.cloneNode(true);
					copy.setAttribute('aria-hidden', 'true');
					copy.querySelectorAll('a').forEach((link) => {
						link.tabIndex = -1;
					});
					track.append(copy);
				}
				passes++;
			}
		};

		const tune = () => {
			fill();
			// Half the track is one full cycle: that is the distance travelled.
			const distance = track.scrollWidth / 2;
			if (!distance) return;
			track.style.setProperty('--marquee-duration', `${Math.max(8, Math.round(distance / speed))}s`);
		};

		tune();
		// Web fonts land after first paint and change every width underneath us.
		document.fonts?.ready.then(tune);
		window.addEventListener('resize', tune, { passive: true });
	});
}
